export interface Vec2 {
  x: number;
  z: number;
}

function lerp(a: Vec2, b: Vec2, localT: number): Vec2 {
  return { x: a.x + (b.x - a.x) * localT, z: a.z + (b.z - a.z) * localT };
}

/** design/gdd/run-start-flythrough.md Formula 3 — standard smoothstep easing, so a look-target lerp doesn't snap direction linearly at every waypoint. */
function easeInOut(x: number): number {
  const c = Math.min(1, Math.max(0, x));
  return c * c * (3 - 2 * c);
}

/**
 * design/gdd/run-start-flythrough.md Formulas 1-3 — samples a position and
 * look-target along the server-supplied waypoint sequence at normalized
 * progress `t` (0..1). Position advances by arc length (Formula 2: constant
 * speed globally, so each segment's share of playback time matches its
 * share of total path length under a fixed total duration) — `pos` is a
 * linear lerp within its segment. `lookAtPos` eases across the same segment
 * (Formula 3) so direction changes at waypoint transitions read as a smooth
 * turn rather than a snap.
 */
export function samplePathPosition(waypoints: readonly Vec2[], t: number): { pos: Vec2; lookAtPos: Vec2 } {
  if (waypoints.length === 0) return { pos: { x: 0, z: 0 }, lookAtPos: { x: 0, z: 0 } };
  if (waypoints.length === 1) return { pos: waypoints[0], lookAtPos: waypoints[0] };

  const clampedT = Math.min(1, Math.max(0, t));
  const segmentLengths: number[] = [];
  let totalLength = 0;
  for (let i = 0; i < waypoints.length - 1; i++) {
    const len = Math.hypot(waypoints[i + 1].x - waypoints[i].x, waypoints[i + 1].z - waypoints[i].z);
    segmentLengths.push(len);
    totalLength += len;
  }

  const targetDistance = clampedT * totalLength;
  let traveled = 0;
  for (let i = 0; i < segmentLengths.length; i++) {
    const isLastSegment = i === segmentLengths.length - 1;
    if (targetDistance <= traveled + segmentLengths[i] || isLastSegment) {
      const localT = segmentLengths[i] > 0 ? Math.min(1, (targetDistance - traveled) / segmentLengths[i]) : 1;
      const a = waypoints[i];
      const b = waypoints[i + 1];
      return { pos: lerp(a, b, localT), lookAtPos: lerp(a, b, easeInOut(localT)) };
    }
    traveled += segmentLengths[i];
  }
  const last = waypoints[waypoints.length - 1];
  return { pos: last, lookAtPos: last };
}
